import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Define translations inline to avoid import issues
const resources = {
  en: {
    translation: {
      "nav": {
        "services": "Services",
        "about": "About",
        "contact": "Contact",
        "getStarted": "Get Started",
        "businessSolutions": "Business Solutions Platform"
      },
      "hero": {
        "badge": "Complete Business Solutions Platform",
        "title": {
          "transform": "Transform Your",
          "business": "Business Operations"
        },
        "subtitle": "From recruitment and compliance to international trade and market intelligence.",
        "subtitleHighlight": "One platform, unlimited possibilities.",
        "cta": {
          "explore": "🚀 Explore Solutions"
        },
        "stats": {
          "clients": "Happy Clients",
          "successRate": "Success Rate",
          "support": "Support",
          "services": "Services"
        }
      },
      "services": {
        "title": "Professional Business Solutions",
        "subtitle": "Choose from our comprehensive suite of services designed to streamline operations and accelerate growth",
        "hrTemplates": {
          "name": "HR Templates",
          "description": "Custom branded application forms and hiring pages",
          "shortDescription": "Get Your Branded Hiring Page – From €1,000",
          "pricing": "From €1,000",
          "cta": "Start Building",
          "features": [
            "Custom branded application forms",
            "Multi-step application process",
            "Mobile-responsive design"
          ]
        },
        "compliance": {
          "name": "Compliance & Insurance",
          "description": "EU compliance monitoring and product recall insurance",
          "shortDescription": "Protect Your Business from EU Recalls",
          "pricing": "From €500/month",
          "cta": "Get Protected",
          "features": [
            "EU compliance monitoring",
            "Product recall insurance",
            "Legal documentation"
          ]
        },
        "rareEarth": {
          "name": "Rare Earth Permits",
          "description": "Secure verified rare-earth export permits with international compliance",
          "shortDescription": "Secure Verified Rare-Earth Export Permits",
          "pricing": "From €3,500",
          "cta": "Get Permits",
          "features": [
            "Rare-earth export permits",
            "International compliance",
            "Expert documentation"
          ]
        },
        "trade": {
          "name": "5G Recommerce",
          "description": "AI-powered 5G technology solutions and hardware sourcing",
          "shortDescription": "AI-Powered 5G Technology Solutions",
          "pricing": "From €2,500",
          "cta": "Explore 5G",
          "features": [
            "5G hardware sourcing",
            "AI-powered matching",
            "Verified supplier network"
          ]
        },
        "intelligence": {
          "name": "Business Intelligence",
          "description": "AI-powered job scraping and company hiring insights",
          "shortDescription": "Discover Hiring Companies Instantly",
          "pricing": "From €200/month",
          "cta": "Start Discovery",
          "features": [
            "AI-powered job scraping",
            "Company hiring insights",
            "Lead generation tools"
          ]
        },
        "verpackg": {
          "name": "VerpackG-Löschung",
          "description": "Complete LUCID de-registration and German packaging compliance",
          "shortDescription": "Eliminate German Packaging Compliance Liability",
          "pricing": "€390 one-time",
          "cta": "Start De-registration",
          "features": [
            "Complete LUCID de-registration",
            "Authorized German representation",
            "Legal compliance guarantee"
          ]
        },
        "status": {
          "available": "Available",
          "online": "Online"
        }
      },
      "about": {
        "title": "Why Choose Mernaki?",
        "subtitle": "We're not just another service provider. We're your strategic partner in business transformation, offering cutting-edge solutions backed by years of expertise and innovation.",
        "features": {
          "precision": {
            "title": "Precision Focus",
            "description": "Tailored solutions that address your specific business challenges with surgical precision."
          },
          "speed": {
            "title": "Lightning Fast",
            "description": "Rapid deployment and implementation to get your business moving at the speed of innovation."
          },
          "security": {
            "title": "Enterprise Security",
            "description": "Bank-level security and compliance standards to protect your business and data."
          }
        }
      },
      "cta": {
        "title": "Ready to Transform Your Business?",
        "subtitle": "Join thousands of businesses that trust Mernaki for their operational excellence and growth.",
        "viewServices": "📋 View All Services"
      },
      "footer": {
        "description": "Your trusted partner for comprehensive business solutions. From recruitment to compliance, international trade to market intelligence - we've got you covered.",
        "services": "Services",
        "contact": "Contact",
        "email": "info@mernaki.de",
        "phone": "+49 155 10078340",
        "location": "Ludwigstraße 8, 85354 Freising, Bavaria, Germany",
        "support": "24/7 Support Available",
        "copyright": "© 2024 Mernaki. All rights reserved. | Transforming businesses worldwide."
      }
    }
  },
  zh: {
    translation: {
      "nav": {
        "services": "服务",
        "about": "关于我们",
        "contact": "联系我们",
        "getStarted": "开始使用",
        "businessSolutions": "商业解决方案平台"
      },
      "hero": {
        "badge": "完整的商业解决方案平台",
        "title": {
          "transform": "转型您的",
          "business": "商业运营"
        },
        "subtitle": "从招聘和合规到国际贸易和市场情报。",
        "subtitleHighlight": "一个平台，无限可能。",
        "cta": {
          "explore": "🚀 探索解决方案",
          "demo": "📊 查看演示"
        },
        "stats": {
          "clients": "满意客户",
          "successRate": "成功率",
          "support": "支持",
          "services": "服务"
        }
      },
      "services": {
        "title": "专业商业解决方案",
        "subtitle": "从我们全面的服务套件中选择，旨在简化运营并加速增长",
        "hrTemplates": {
          "name": "人力资源模板",
          "description": "定制品牌申请表和招聘页面",
          "shortDescription": "获取您的品牌招聘页面 – 从 €1,000 起",
          "pricing": "从 €1,000 起",
          "cta": "开始构建",
          "features": [
            "定制品牌申请表",
            "多步骤申请流程",
            "移动响应式设计"
          ]
        },
        "compliance": {
          "name": "合规与保险",
          "description": "欧盟合规监控和产品召回保险",
          "shortDescription": "保护您的企业免受欧盟召回影响",
          "pricing": "从 €500/月 起",
          "cta": "获得保护",
          "features": [
            "欧盟合规监控",
            "产品召回保险",
            "法律文档"
          ]
        },
        "rareEarth": {
          "name": "稀土许可证",
          "description": "获得经过验证的稀土出口许可证和国际合规",
          "shortDescription": "获得经过验证的稀土出口许可证",
          "pricing": "从 €3,500 起",
          "cta": "获取许可证",
          "features": [
            "稀土出口许可证",
            "国际合规",
            "专家文档"
          ]
        },
        "trade": {
          "name": "5G 再商务",
          "description": "AI 驱动的 5G 技术解决方案和硬件采购",
          "shortDescription": "AI 驱动的 5G 技术解决方案",
          "pricing": "从 €2,500 起",
          "cta": "探索 5G",
          "features": [
            "5G 硬件采购",
            "AI 驱动匹配",
            "经过验证的供应商网络"
          ]
        },
        "intelligence": {
          "name": "商业智能",
          "description": "AI 驱动的职位抓取和公司招聘洞察",
          "shortDescription": "即时发现招聘公司",
          "pricing": "从 €200/月 起",
          "cta": "开始发现",
          "features": [
            "AI 驱动的职位抓取",
            "公司招聘洞察",
            "潜在客户生成工具"
          ]
        },
        "verpackg": {
          "name": "VerpackG 注销",
          "description": "完整的 LUCID 注销和德国包装合规",
          "shortDescription": "消除德国包装合规责任",
          "pricing": "€390 一次性",
          "cta": "开始注销",
          "features": [
            "完整的 LUCID 注销",
            "授权德国代表",
            "法律合规保证"
          ]
        },
        "status": {
          "available": "可用",
          "online": "在线"
        }
      },
      "about": {
        "title": "为什么选择 Mernaki？",
        "subtitle": "我们不仅仅是另一个服务提供商。我们是您商业转型的战略合作伙伴，提供由多年专业知识和创新支持的尖端解决方案。",
        "features": {
          "precision": {
            "title": "精准专注",
            "description": "量身定制的解决方案，以外科手术般的精确度解决您的特定业务挑战。"
          },
          "speed": {
            "title": "闪电般快速",
            "description": "快速部署和实施，让您的业务以创新的速度前进。"
          },
          "security": {
            "title": "企业级安全",
            "description": "银行级安全和合规标准，保护您的业务和数据。"
          }
        }
      },
      "cta": {
        "title": "准备好转型您的业务了吗？",
        "subtitle": "加入数千家信任 Mernaki 实现卓越运营和增长的企业。",
        "startJourney": "🚀 开始您的旅程",
        "viewServices": "📋 查看所有服务"
      },
      "footer": {
        "description": "您值得信赖的全面商业解决方案合作伙伴。从招聘到合规，国际贸易到市场情报 - 我们为您提供全方位服务。",
        "services": "服务",
        "contact": "联系方式",
        "email": "support@mernaki.de",
        "phone": "+49 (0) 123 456 789",
        "location": "德国柏林",
        "support": "24/7 全天候支持",
        "copyright": "© 2024 Mernaki. 保留所有权利。| 在全球范围内转型企业。"
      }
    }
  },
  de: {
    translation: {
      "nav": {
        "services": "Dienstleistungen",
        "about": "Über uns",
        "contact": "Kontakt",
        "getStarted": "Loslegen",
        "businessSolutions": "Business-Lösungsplattform"
      },
      "hero": {
        "badge": "Komplette Business-Lösungsplattform",
        "title": {
          "transform": "Transformieren Sie Ihre",
          "business": "Geschäftsabläufe"
        },
        "subtitle": "Von Rekrutierung und Compliance bis hin zu internationalem Handel und Marktintelligenz.",
        "subtitleHighlight": "Eine Plattform, unbegrenzte Möglichkeiten.",
        "cta": {
          "explore": "🚀 Lösungen erkunden",
          "demo": "📊 Demo ansehen"
        },
        "stats": {
          "clients": "Zufriedene Kunden",
          "successRate": "Erfolgsquote",
          "support": "Support",
          "services": "Dienstleistungen"
        }
      },
      "services": {
        "title": "Professionelle Business-Lösungen",
        "subtitle": "Wählen Sie aus unserem umfassenden Service-Portfolio, das darauf ausgelegt ist, Abläufe zu optimieren und Wachstum zu beschleunigen",
        "hrTemplates": {
          "name": "HR-Vorlagen",
          "description": "Maßgeschneiderte Bewerbungsformulare und Recruiting-Seiten",
          "shortDescription": "Erhalten Sie Ihre Marken-Recruiting-Seite – Ab €1.000",
          "pricing": "Ab €1.000",
          "cta": "Aufbau starten",
          "features": [
            "Maßgeschneiderte Bewerbungsformulare",
            "Mehrstufiger Bewerbungsprozess",
            "Mobile-responsive Design"
          ]
        },
        "compliance": {
          "name": "Compliance & Versicherung",
          "description": "EU-Compliance-Überwachung und Produktrückruf-Versicherung",
          "shortDescription": "Schützen Sie Ihr Unternehmen vor EU-Rückrufen",
          "pricing": "Ab €500/Monat",
          "cta": "Schutz erhalten",
          "features": [
            "EU-Compliance-Überwachung",
            "Produktrückruf-Versicherung",
            "Rechtsdokumentation"
          ]
        },
        "rareEarth": {
          "name": "Seltene Erden Genehmigungen",
          "description": "Sichere verifizierte Seltene-Erden-Exportgenehmigungen mit internationaler Compliance",
          "shortDescription": "Sichere verifizierte Seltene-Erden-Exportgenehmigungen",
          "pricing": "Ab €3.500",
          "cta": "Genehmigungen erhalten",
          "features": [
            "Seltene-Erden-Exportgenehmigungen",
            "Internationale Compliance",
            "Expertendokumentation"
          ]
        },
        "trade": {
          "name": "5G Recommerce",
          "description": "KI-gestützte 5G-Technologielösungen und Hardware-Beschaffung",
          "shortDescription": "KI-gestützte 5G-Technologielösungen",
          "pricing": "Ab €2.500",
          "cta": "5G erkunden",
          "features": [
            "5G-Hardware-Beschaffung",
            "KI-gestütztes Matching",
            "Verifiziertes Lieferantennetzwerk"
          ]
        },
        "intelligence": {
          "name": "Business Intelligence",
          "description": "KI-gestütztes Job-Scraping und Unternehmens-Recruiting-Einblicke",
          "shortDescription": "Entdecken Sie sofort rekrutierende Unternehmen",
          "pricing": "Ab €200/Monat",
          "cta": "Entdeckung starten",
          "features": [
            "KI-gestütztes Job-Scraping",
            "Unternehmens-Recruiting-Einblicke",
            "Lead-Generierung-Tools"
          ]
        },
        "verpackg": {
          "name": "VerpackG-Löschung",
          "description": "Vollständige LUCID-Abmeldung und deutsche Verpackungs-Compliance",
          "shortDescription": "Eliminieren Sie deutsche Verpackungs-Compliance-Haftung",
          "pricing": "€390 einmalig",
          "cta": "Abmeldung starten",
          "features": [
            "Vollständige LUCID-Abmeldung",
            "Autorisierte deutsche Vertretung",
            "Rechtliche Compliance-Garantie"
          ]
        },
        "status": {
          "available": "Verfügbar",
          "online": "Online"
        }
      },
      "about": {
        "title": "Warum Mernaki wählen?",
        "subtitle": "Wir sind nicht nur ein weiterer Dienstleister. Wir sind Ihr strategischer Partner bei der Geschäftstransformation und bieten modernste Lösungen, die von jahrelanger Expertise und Innovation unterstützt werden.",
        "features": {
          "precision": {
            "title": "Präzisionsfokus",
            "description": "Maßgeschneiderte Lösungen, die Ihre spezifischen Geschäftsherausforderungen mit chirurgischer Präzision angehen."
          },
          "speed": {
            "title": "Blitzschnell",
            "description": "Schnelle Bereitstellung und Implementierung, um Ihr Unternehmen mit der Geschwindigkeit der Innovation voranzubringen."
          },
          "security": {
            "title": "Unternehmenssicherheit",
            "description": "Sicherheits- und Compliance-Standards auf Bankniveau zum Schutz Ihres Unternehmens und Ihrer Daten."
          }
        }
      },
      "cta": {
        "title": "Bereit, Ihr Unternehmen zu transformieren?",
        "subtitle": "Schließen Sie sich Tausenden von Unternehmen an, die Mernaki für ihre operative Exzellenz und ihr Wachstum vertrauen.",
        "startJourney": "🚀 Ihre Reise beginnen",
        "viewServices": "📋 Alle Services anzeigen"
      },
      "footer": {
        "description": "Ihr vertrauensvoller Partner für umfassende Business-Lösungen. Von Rekrutierung bis Compliance, internationalem Handel bis Marktintelligenz - wir haben Sie abgedeckt.",
        "services": "Dienstleistungen",
        "contact": "Kontakt",
        "email": "support@mernaki.de",
        "phone": "+49 (0) 123 456 789",
        "location": "Berlin, Deutschland",
        "support": "24/7 Support verfügbar",
        "copyright": "© 2024 Mernaki. Alle Rechte vorbehalten. | Unternehmen weltweit transformieren."
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: true, // Enable debug for troubleshooting
    
    interpolation: {
      escapeValue: false, // React already does escaping
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
    
    react: {
      useSuspense: false,
    }
  })
  .then(() => {
    console.log('i18n initialized successfully');
    console.log('Available languages:', Object.keys(resources));
    console.log('Current language:', i18n.language);
  })
  .catch((error) => {
    console.error('i18n initialization failed:', error);
  });

export default i18n;